import Konva from 'konva';

export const SCALE_STEP = 1.1;

/**
 * @description: get distance between 2 points, e.g. for multi touch event
 * original source https://konvajs.org/docs/sandbox/Multi-touch_Scale_Stage.html
 */
export function getDistance(p1: Konva.Vector2d | null, p2: Konva.Vector2d | null): number {
  if (!p1 || !p2) return 0;
  return Math.sqrt((p2.y - p1.y) ** 2 + (p2.x - p1.x) ** 2);
}

/**
 * original source https://konvajs.org/docs/sandbox/Multi-touch_Scale_Stage.html
 */
export function getCenter(p1: Konva.Vector2d | null, p2: Konva.Vector2d | null): Konva.Vector2d {
  if (!p1 || !p2) return { x: 0, y: 0 };
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

export const scaleUp = (scale: number) => scale * SCALE_STEP;
export const scaleDown = (scale: number) => scale / SCALE_STEP;

export interface KonvaZoomOptions {
  minScale?: number;
  maxScale?: number;
}

const defaultZoomOptions: Required<KonvaZoomOptions> = {
  minScale: 0.4,
  maxScale: 3,
};

/**
 * Wheel zoom for desktop. Zoom toward cursor, update scale and position.
 * Calls setScale and setPosition to sync React state.
 */
export function handleZoomChange(
  stage: Konva.Stage,
  setScale: (s: number) => void,
  setPosition: (p: { x: number; y: number }) => void,
  options: KonvaZoomOptions = {},
  setIsAutoScale?: (value: boolean) => void,
): () => void {
  const { minScale, maxScale } = { ...defaultZoomOptions, ...options };

  const onWheel = (e: { evt: WheelEvent }) => {
    e.evt.preventDefault();
    const currentScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    if (pointer) {
      const rawScale = e.evt.deltaY > 0 ? scaleUp(currentScale) : scaleDown(currentScale);
      const scale = Math.max(minScale, Math.min(maxScale, rawScale));

      // prevent further upscale/downscale
      if (scale === currentScale) return;

      // Отключаем auto scale при ручном зуме
      if (setIsAutoScale) {
        setIsAutoScale(false);
      }

      // Формула из рабочего кода: получаем точку в локальных координатах контента
      const mousePointTo = {
        x: pointer.x / currentScale - stage.x() / currentScale,
        y: pointer.y / currentScale - stage.y() / currentScale,
      };
      // Вычисляем новую позицию так, чтобы та же точка осталась под курсором
      const newPos = {
        x: -(mousePointTo.x - pointer.x / scale) * scale,
        y: -(mousePointTo.y - pointer.y / scale) * scale,
      };

      stage.scaleX(scale);
      stage.scaleY(scale);
      stage.position(newPos);
      stage.batchDraw();

      setScale(scale);
      setPosition(newPos);
    }
  };

  stage.on('wheel', onWheel);

  return () => {
    stage.off('wheel', onWheel);
  };
}

/**
 * Pinch-to-zoom via touch. Updates stage during gesture, syncs setScale and setPosition on touchend.
 * Uses requestAnimationFrame to throttle batchDraw. Clamps scale to minScale/maxScale.
 */
export function handleTouchMove(
  stage: Konva.Stage,
  setScale: (s: number) => void,
  setPosition: (p: { x: number; y: number }) => void,
  options: KonvaZoomOptions = {},
  setIsAutoScale?: (value: boolean) => void,
): () => void {
  const { minScale, maxScale } = { ...defaultZoomOptions, ...options };

  let lastCenter: Konva.Vector2d | null = null;
  let lastDist = 0;
  let rafId: number | null = null;

  const setDefaults = () => {
    lastDist = 0;
    lastCenter = null;
  };

  const scheduleBatchDraw = () => {
    if (rafId != null) return;
    rafId = requestAnimationFrame(() => {
      stage.batchDraw();
      rafId = null;
    });
  };

  const syncToState = () => {
    const finalScale = stage.scaleX();
    setScale(finalScale);
    setPosition(stage.position());

    // Если масштаб достиг минимума (или очень близок к нему), включаем auto-scale
    // чтобы схема вернулась к исходному размеру при уменьшении
    // Используем небольшой порог для учета погрешностей вычислений
    const scaleThreshold = minScale * 1.01;
    if (setIsAutoScale && finalScale <= scaleThreshold) {
      setIsAutoScale(true);
    }

    setDefaults();
  };

  const onTouchMove = (e: { evt: TouchEvent; target: Konva.Node }) => {
    const [touch1, touch2] = Array.from(e.evt.touches || []);

    if (touch1 && touch2) {
      // Только при двух пальцах блокируем default и обрабатываем pinch-to-zoom
      e.evt.preventDefault();

      if (stage.isDragging()) {
        stage.stopDrag();
      }

      // Преобразуем координаты из viewport в координаты контейнера Stage
      const stageContainer = stage.container();
      const rect = stageContainer.getBoundingClientRect();

      const p1 = { x: touch1.clientX, y: touch1.clientY };
      const p2 = { x: touch2.clientX, y: touch2.clientY };

      // Преобразуем координаты в координаты контейнера Stage
      const p1Stage = { x: p1.x - rect.left, y: p1.y - rect.top };
      const p2Stage = { x: p2.x - rect.left, y: p2.y - rect.top };

      if (!lastCenter) {
        lastCenter = getCenter(p1Stage, p2Stage);
        return;
      }

      const newCenter = getCenter(p1Stage, p2Stage);
      const dist = getDistance(p1Stage, p2Stage);

      if (!lastDist) {
        lastDist = dist;
      }

      const currentScale = stage.scaleX();
      const rawScale = currentScale * (dist / lastDist);
      const scale = Math.max(minScale, Math.min(maxScale, rawScale));

      // Отключаем auto scale при любом ручном зуме
      if (setIsAutoScale && scale !== currentScale) {
        setIsAutoScale(false);
      }

      // Проверяем, действительно ли масштаб изменился
      // Используем проверку с учетом погрешностей вычислений с плавающей точкой
      const scaleChanged = Math.abs(scale - currentScale) > 0.0001;

      // Если масштаб не изменился (достигнут предел), не обновляем позицию
      // чтобы избежать дерганий схемы при попытке продолжить зум за пределы
      if (!scaleChanged) {
        // Масштаб не изменился - обновляем только lastDist и lastCenter для следующей итерации
        lastDist = dist;
        lastCenter = newCenter;
        return;
      }

      // Дополнительная проверка: если пользователь пытается продолжить зум за пределы,
      // и масштаб уже на пределе, не обновляем позицию, чтобы избежать дерганий
      const isAtMinLimit = rawScale < minScale && scale === minScale && currentScale === minScale;
      const isAtMaxLimit = rawScale > maxScale && scale === maxScale && currentScale === maxScale;

      if (isAtMinLimit || isAtMaxLimit) {
        // Пользователь пытается продолжить зум за пределы, но масштаб уже на пределе
        // Обновляем только lastDist и lastCenter, но не обновляем позицию
        lastDist = dist;
        lastCenter = newCenter;
        return;
      }

      // Используем формулу, аналогичную handleZoomChange для правильного зума к точке
      // Получаем точку в локальных координатах контента (до зума)
      const pointTo = {
        x: newCenter.x / currentScale - stage.x() / currentScale,
        y: newCenter.y / currentScale - stage.y() / currentScale,
      };

      // Вычисляем новую позицию так, чтобы та же точка осталась под центром жеста
      const newPos = {
        x: -(pointTo.x - newCenter.x / scale) * scale,
        y: -(pointTo.y - newCenter.y / scale) * scale,
      };

      stage.scaleX(scale);
      stage.scaleY(scale);
      stage.position(newPos);
      scheduleBatchDraw();

      lastDist = dist;
      lastCenter = newCenter;
    }
    // При одном пальце не вызываем preventDefault - позволяем Konva drag работать
  };

  const onTouchEnd = (e: { evt: TouchEvent }) => {
    if ((e.evt.touches?.length ?? 0) < 2) {
      syncToState();
    }
  };

  stage.on('touchmove', onTouchMove);
  stage.on('touchend', onTouchEnd);
  stage.on('touchcancel', onTouchEnd);

  return () => {
    stage.off('touchmove', onTouchMove);
    stage.off('touchend', onTouchEnd);
    stage.off('touchcancel', onTouchEnd);
  };
}
